import {
  FAQ_Category,
  FAQ_CATEGORIES,
  FAQ_PARAM,
  FaqsService,
} from "@/apps/credit-line/services/multi-country/faqs.service";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";

/**
 * FAQ item structure from backend settings
 */
export interface FaqItem {
  id: string;
  q: string; // translation key for question
  a: string; // translation key for answer
}

/**
 * FAQ settings structure from backend
 * Maps category names to arrays of FAQ items
 */
export interface FaqSettings {
  [category: string]: FaqItem[];
}

/**
 * Country-specific values for FAQ placeholders
 */
export interface FaqCountryValues {
  id_type: string;
  id_type_full: string;
  pickup_location: string;
  pickup_provider: string;
  payment_frequency: string;
  payment_frequency_days: string;
  atm_banks: string;
  support_whatsapp: string;
  payment_methods: string;
  bank_transfer_system: string;
  bank_validation_url: string;
  min_withdrawal: string;
  pin_recovery_method: string;
  pin_activation_method: string;
  country_name: string;
  [key: string]: string;
}

/**
 * Parsed FAQ settings including rollout flag
 */
export interface ParsedFaqSettings {
  enabled: boolean;
  faqConfig: FaqSettings | null;
  countryValues: FaqCountryValues | null;
}

/**
 * Settings category for FAQ - uses CL_FAQ_SETTINGS which returns faqSettings
 */
const FAQ_SETTINGS_CATEGORY = SettingsCategory.CL_FAQ_SETTINGS;

// Map FAQ_PARAM filter to corresponding category names
const FAQ_PARAM_CATEGORY_MAP: Record<FAQ_PARAM, FAQ_CATEGORIES[]> = {
  [FAQ_PARAM.withdraw]: [FAQ_CATEGORIES.WITHDRAWAL],
  [FAQ_PARAM.pay]: [FAQ_CATEGORIES.PAYMENT],
  [FAQ_PARAM.card]: [
    FAQ_CATEGORIES.PAYJOY_CARD,
    FAQ_CATEGORIES.PAYJOY_VIRTUAL_CARD,
  ],
  [FAQ_PARAM.physical_card]: [FAQ_CATEGORIES.PAYJOY_CARD],
  [FAQ_PARAM.virtual_card]: [FAQ_CATEGORIES.PAYJOY_VIRTUAL_CARD],
  [FAQ_PARAM.phone_finance]: [FAQ_CATEGORIES.PHONE_FINANCE],
};

/**
 * Creates a FaqsService-compatible implementation from settings-based FAQ config
 * @param faqConfig The FAQ configuration from backend settings
 * @returns A FaqsService implementation
 */
export function createSettingsFaqService(faqConfig: FaqSettings): FaqsService {
  const categories: FAQ_Category[] = Object.entries(faqConfig).map(
    ([name, items]) => ({
      name,
      itemsAmount: items.length,
    }),
  );

  return {
    categories,

    getCategoryItemsAmount(category: string): number {
      return faqConfig[category]?.length || 0;
    },

    getCategories(filter: FAQ_PARAM): FAQ_Category[] {
      if (!filter) {
        return categories;
      }

      const allowedCategories = FAQ_PARAM_CATEGORY_MAP[filter];
      if (!allowedCategories) {
        return categories;
      }

      return categories.filter((cat) =>
        allowedCategories.includes(cat.name as FAQ_CATEGORIES),
      );
    },

    getQuestions(category: string): string[] {
      const items = faqConfig[category];
      if (!items) return [];
      return items.map((item) => item.q);
    },

    getAnswers(category: string): string[] {
      const items = faqConfig[category];
      if (!items) return [];
      return items.map((item) => item.a);
    },
  };
}

/**
 * Load FAQ settings from backend
 * Uses the same pattern as useNewShipmentsUi - reads from CL_HELP_PAGE settings
 * and checks the faqSettings.enabled flag and faqSettings.values.faqData
 *
 * @returns Promise with parsed FAQ settings or null if unavailable
 */
export async function loadFaqSettings(): Promise<ParsedFaqSettings> {
  try {
    const settings = await getSettings(FAQ_SETTINGS_CATEGORY);

    // Get faqSettings from the response (follows same pattern as useNewShipmentsUi)
    const faqSettingsValue = settings["faqSettings"];

    // Check if the feature is enabled (rollout selector passed)
    const enabled = faqSettingsValue?.enabled ?? false;

    // Extract FAQ config from values.faqData (external values are resolved by backend)
    const faqConfig =
      enabled && faqSettingsValue?.values?.faqData
        ? (faqSettingsValue.values.faqData as unknown as FaqSettings)
        : null;

    // Extract country-specific placeholder values from values.countryValues
    const countryValues =
      enabled && faqSettingsValue?.values?.countryValues
        ? (faqSettingsValue.values.countryValues as unknown as FaqCountryValues)
        : null;

    return {
      enabled,
      faqConfig,
      countryValues,
    };
  } catch (error) {
    console.error("Failed to load FAQ settings:", error);
    return {
      enabled: false,
      faqConfig: null,
      countryValues: null,
    };
  }
}
